import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Avatar } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { UserAdd01Icon } from "@hugeicons-pro/core-stroke-rounded";
import RelationshipType from "enums/RelationshipType";
import RenderRelationship from "components/common/contacts/RenderRelationship";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { activateOnKey } from "utils/activateOnKey";
import CustomSkeleton from "components/common/CustomSkeleton";

export interface LinkedIndividual {
  id: string;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  relationshipType: RelationshipType | null;
}

interface ContactPeopleCardProps {
  people: LinkedIndividual[];
  loading: boolean;
  onAddPerson: () => void;
  onOpenPerson: (contactId: string) => void;
}

const ContactPeopleCard: React.FC<ContactPeopleCardProps> = ({
  people,
  loading,
  onAddPerson,
  onOpenPerson,
}) => {
  const { t } = useTranslation();

  return (
    <Card variant="outlined" sx={{ padding: "var(--space-6)" }}>
      <Stack gap="var(--space-5)">
        <Inline justify="between" align="center">
          <Typography variant="h5">
            {t("PeopleWithCount", { count: people.length })}
          </Typography>
          <Button
            variant="primary-outlined"
            size="small"
            startIcon={<HugeiconsIcon icon={UserAdd01Icon} size={16} />}
            onClick={onAddPerson}
          >
            {t("Add person")}
          </Button>
        </Inline>

        {loading ? (
          <CustomSkeleton variant="user" rows={2} />
        ) : people.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: "var(--color-text-tertiary)",
              textAlign: "center",
              paddingBlock: "var(--space-6)",
              border: "1px dashed var(--color-border-subtle)",
              borderRadius: "var(--radius-md)",
            }}
          >
            {t("No individuals linked to this business yet")}
          </Typography>
        ) : (
          <Stack gap="var(--space-2)">
            {people.map((person) => {
              const name = [person.firstName, person.lastName]
                .filter(Boolean)
                .join(" ");
              return (
                // TODO: Replace this with ListItemButton component once it's available
                <Inline
                  key={person.id}
                  justify="between"
                  align="center"
                  wrap={false}
                  role="button"
                  tabIndex={0}
                  aria-label={t("ViewContactDetailsFor", { name })}
                  onClick={() => onOpenPerson(person.id)}
                  onKeyDown={activateOnKey(() => onOpenPerson(person.id))}
                  sx={{
                    cursor: "pointer",
                    borderRadius: "var(--radius-md)",
                    padding: "var(--space-4)",
                    transition: "background-color 150ms ease-in-out",
                    "&:hover": {
                      backgroundColor: "var(--color-bg-surface-hover)",
                    },
                  }}
                >
                  <Inline gap="var(--space-3)" align="center" wrap={false}>
                    <Avatar size="md" name={name} />
                    <Stack gap="var(--space-1)">
                      <Typography variant="subtitle2">{name}</Typography>
                      {person.title && (
                        <Typography
                          variant="caption"
                          sx={{ color: "var(--color-text-tertiary)" }}
                        >
                          {person.title}
                        </Typography>
                      )}
                    </Stack>
                  </Inline>
                  <RenderRelationship relationship={person.relationshipType} />
                </Inline>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

export default ContactPeopleCard;
